import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Menu, X } from 'lucide-react';

interface PublicHeaderProps {
  activeLink?: 'features' | 'pricing' | 'about' | 'contact' | 'login' | 'signup';
}

const PublicHeader: React.FC<PublicHeaderProps> = ({ activeLink }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 768);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const styles = {
    nav: {
      borderBottom: '1px solid #e8eaed',
      padding: '16px 0',
      position: 'sticky' as const,
      top: 0,
      zIndex: 1000,
      backdropFilter: 'blur(10px)',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
    },
    navContainer: {
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '0 16px',
      display: 'flex',
      justifyContent: isMobile ? 'flex-start' : 'space-between',
      alignItems: 'center',
      gap: isMobile ? '12px' : '0',
    },
    logo: {
      fontSize: isMobile ? '20px' : '24px',
      fontWeight: 700,
      color: '#14B8A6',
      textDecoration: 'none',
      letterSpacing: '-0.5px',
    },
    navLinks: {
      display: 'flex',
      gap: '32px',
      alignItems: 'center',
    },
    navLink: {
      color: '#5f6368',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: 500,
      transition: 'color 0.2s',
    },
    activeNavLink: {
      color: '#14B8A6',
      fontWeight: 600,
    },
    mobileMenu: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      width: '80%',
      maxWidth: '400px',
      height: '100vh',
      background: '#ffffff',
      zIndex: 2000,
      padding: '80px 24px 24px',
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '24px',
    },
    mobileMenuOverlay: {
      position: 'fixed' as const,
      top: 0,
      left: 0,
      width: '100%',
      height: '100vh',
      background: 'rgba(0,0,0,0.5)',
      zIndex: 1999,
    },
    hamburger: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
    },
    closeButton: {
      position: 'absolute' as const,
      top: '16px',
      right: '16px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '8px',
    },
    primaryButton: {
      background: '#14B8A6',
      color: '#ffffff',
      padding: '12px 24px',
      borderRadius: '24px',
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: 600,
      border: 'none',
      cursor: 'pointer',
      transition: 'all 0.2s',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: '0 1px 2px 0 rgba(60,64,67,0.3), 0 1px 3px 1px rgba(60,64,67,0.15)',
    },
  };

  const getLinkStyle = (link: string) => {
    return activeLink === link
      ? { ...styles.navLink, ...styles.activeNavLink }
      : styles.navLink;
  };

  return (
    <>
      <nav style={styles.nav}>
        <div style={styles.navContainer}>
          {isMobile && (
            <button style={styles.hamburger} onClick={() => setMobileMenuOpen(true)}>
              <Menu size={24} color="#202124" />
            </button>
          )}
          
          <Link to="/" style={styles.logo}>HUSLRZ</Link>
          
          {!isMobile && (
            <div style={styles.navLinks}>
              <Link to="/partners" style={getLinkStyle('partners')}>For Partners</Link>
              <Link to="/corporate" style={getLinkStyle('corporate')}>For Corporates</Link>
              <Link to="/about" style={getLinkStyle('about')}>About</Link>
              <Link to="/contact" style={getLinkStyle('contact')}>Contact</Link>
              <Link to="/login" style={getLinkStyle('login')}>Login</Link>
              <Link to="/signup" style={styles.primaryButton}>
                Get Started
                <ArrowRight size={16} />
              </Link>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobile && mobileMenuOpen && (
        <>
          <div style={styles.mobileMenuOverlay} onClick={() => setMobileMenuOpen(false)} />
          <div style={styles.mobileMenu}>
            <button style={styles.closeButton} onClick={() => setMobileMenuOpen(false)}>
              <X size={24} color="#202124" />
            </button>
            <Link to="/partners" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Partners</Link>
            <Link to="/corporate" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>For Corporates</Link>
            <Link to="/about" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>About</Link>
            <Link to="/contact" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0' }} onClick={() => setMobileMenuOpen(false)}>Contact</Link>
            <Link to="/login" style={{ ...styles.navLink, fontSize: '18px', padding: '12px 0', color: '#14B8A6', fontWeight: 600 }} onClick={() => setMobileMenuOpen(false)}>Login</Link>
            <Link to="/signup" style={{ ...styles.primaryButton, width: '100%', justifyContent: 'center', padding: '16px 24px', fontSize: '16px', marginTop: '16px' }} onClick={() => setMobileMenuOpen(false)}>
              Get Started
              <ArrowRight size={18} />
            </Link>
          </div>
        </>
      )}
    </>
  );
};

export default PublicHeader;
